import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  MapPin, 
  DollarSign, 
  TrendingUp,
  CheckCircle,
  XCircle,
  Loader2,
  Briefcase,
  Telescope,
  Heart
} from 'lucide-react';

interface Startup {
  id: number;
  name: string;
  company_name: string;
  business_description: string;
  industries: string[];
  stage: string;
  funding_amount: string;
  location: string;
  website?: string;
}

interface Match {
  id: number;
  startup_id: number;
  status: 'interested' | 'passed';
  company_name: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export function InvestorMatching() {
  const { token } = useAuth();
  const [startups, setStartups] = useState<Startup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [matches, setMatches] = useState<Match[]>([]);
  const [showMatches, setShowMatches] = useState(false);

  // Fetch startups
  const fetchStartups = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/matches/startups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStartups(data.startups);
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('Failed to fetch startups:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Fetch existing matches
  const fetchMatches = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/matches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMatches(data.matches);
      }
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchStartups();
    fetchMatches();
  }, [fetchStartups, fetchMatches]);

  // Handle match action
  const handleMatch = async (status: 'interested' | 'passed') => {
    if (isProcessing || !currentStartup) return;
    
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/matches`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          startupId: currentStartup.id,
          status
        }),
      });
      
      if (response.ok) {
        // Add to matches list
        setMatches(prev => [...prev, {
          id: Date.now(),
          startup_id: currentStartup.id,
          status,
          company_name: currentStartup.company_name
        }]);
        
        // Move to next startup
        setCurrentIndex(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to create match:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const currentStartup = startups[currentIndex];
  const hasMoreStartups = currentIndex < startups.length;
  const interestedCount = matches.filter(m => m.status === 'interested').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Telescope className="w-6 h-6 text-primary" />
            Discover Startups
          </h2>
          <p className="text-muted-foreground">
            {hasMoreStartups 
              ? `Startup ${currentIndex + 1} of ${startups.length}`
              : 'No more startups to show'
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMatches(!showMatches)}
          >
            <Heart className="w-4 h-4 mr-2 text-red-500" />
            Interested ({interestedCount})
          </Button>
        </div>
      </div>

      {/* Matches List (toggleable) */}
      <AnimatePresence>
        {showMatches && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <Card className="p-4 bg-muted/50">
              <h3 className="font-semibold mb-3">Your Matches</h3>
              {matches.length === 0 ? (
                <p className="text-muted-foreground text-sm">No matches yet. Start browsing!</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {matches.map(match => (
                    <div 
                      key={match.id}
                      className="flex items-center justify-between p-2 bg-background rounded-lg"
                    >
                      <span className="font-medium">{match.company_name}</span>
                      <Badge variant={match.status === 'interested' ? 'default' : 'secondary'}>
                        {match.status === 'interested' ? 'Interested' : 'Passed'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Startup Card */}
      <AnimatePresence mode="wait">
        {hasMoreStartups && currentStartup ? (
          <motion.div
            key={currentStartup.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="overflow-hidden border-2 hover:border-primary/50 transition-colors">
              {/* Header */}
              <div className="p-6 border-b bg-gradient-to-br from-primary/5 to-transparent">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">{currentStartup.company_name}</h3>
                    <p className="text-muted-foreground">{currentStartup.name}</p>
                  </div>
                  {currentStartup.stage && (
                    <Badge variant="secondary" className="text-sm">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      {currentStartup.stage}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Description */}
                <p className="text-foreground leading-relaxed">
                  {currentStartup.business_description || 'No description provided.'}
                </p>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  {currentStartup.industries?.length > 0 && (
                    <div>
                      <label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        Industry
                      </label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {currentStartup.industries.map(ind => (
                          <Badge key={ind} variant="outline" className="text-xs">
                            {ind}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {currentStartup.funding_amount && (
                    <div>
                      <label className="text-xs text-muted-foreground flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        Funding Ask
                      </label>
                      <p className="font-medium">{currentStartup.funding_amount}</p>
                    </div>
                  )}

                  {currentStartup.location && (
                    <div>
                      <label className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Location
                      </label>
                      <p className="font-medium">{currentStartup.location}</p>
                    </div>
                  )}

                  {currentStartup.website && (
                    <div>
                      <label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        Website
                      </label>
                      <a 
                        href={currentStartup.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm truncate block"
                      >
                        {currentStartup.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="p-6 border-t bg-muted/30 flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  className="flex-1"
                  onClick={() => handleMatch('passed')}
                  disabled={isProcessing}
                >
                  <XCircle className="w-5 h-5 mr-2 text-muted-foreground" />
                  Pass
                </Button>
                <Button
                  size="lg"
                  className="flex-1 bg-gradient-to-r from-primary to-primary/90"
                  onClick={() => handleMatch('interested')}
                  disabled={isProcessing}
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Interested
                </Button>
              </div>
            </Card>
          </motion.div>
        ) : (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
              <Telescope className="w-12 h-12 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {startups.length === 0 ? 'No Startups Found' : 'All Caught Up!'}
            </h3>
            <p className="text-muted-foreground max-w-sm mx-auto mb-6">
              {startups.length === 0 
                ? 'There are no startups matching your preferences at the moment. Check back later!'
                : "You've reviewed all available startups. Great job! Check your matches to follow up."
              }
            </p>
            <Button onClick={fetchStartups} variant="outline">
              Refresh List
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
